import {
  PresentationContent,
  PresentationModification,
  ShapeContent,
  SlideContent,
  ChatMessage,
  ApplyReasonCode,
  DeckLayoutCatalog,
  ThemeColors,
} from "../types";
import { ModelConfig, getSelectedModelId, getModelConfig } from "../modelConfig";
import {
  buildPrimaryRequestEnvelopePrompt,
  buildToolFollowUpEnvelopePrompt,
} from "../services/ai/promptCatalog";

export function getCurrentModel(): ModelConfig {
  const modelId = getSelectedModelId();
  return getModelConfig(modelId) || getModelConfig("gpt-5.5")!;
}

export function attachModelDiagnostic(
  modifications: PresentationModification,
  code: ApplyReasonCode,
  message: string
): void {
  if (!modifications.diagnostics) {
    modifications.diagnostics = [];
  }
  modifications.diagnostics.push({
    code,
    category: "model_output_mismatch",
    message,
  });
}

// ---------------------------------------------------------------------------
// Context envelope — sends the full deck context (with smart truncation)
// to the AI, enabling cross-slide references and global edits.
// ---------------------------------------------------------------------------

const MAX_DECK_CONTEXT_CHARS = 220_000;
const MAX_HISTORY_MESSAGES = 5;

interface SlideContextEntry {
  slideNumber: number;
  index: number;
  id: string;
  layout?: string;
  customLayoutName?: string;
  slideMasterName?: string;
  displayMasterShapes?: boolean;
  followMasterBackground?: boolean;
  shapes: SlideContent["shapes"];
}

interface CurrentSlideContext {
  slideNumber: number;
  index: number;
  id: string;
  shapes: SlideContent["shapes"];
}

interface DeckContext {
  totalSlides: number;
  includedSlideCount: number;
  truncated: boolean;
  omittedSlideNumbers: number[];
  includedSlides: SlideContextEntry[];
}

export interface RequestContextEnvelope {
  selectedSlideIndex: number;
  selectedSlideNumber: number;
  slideWidth?: number;
  slideHeight?: number;
  currentSlideContext: CurrentSlideContext;
  deckContext: DeckContext;
  deckLayoutCatalog?: DeckLayoutCatalog;
  themeColors?: ThemeColors;
}

/**
 * Select the completed conversation to send with a new request. Trailing user
 * turns have no recorded reply (stopped, interrupted, or being replaced) and
 * must never be replayed as conversation.
 */
export function snapshotCompletedChatHistoryForRun(
  messages: readonly ChatMessage[]
): ChatMessage[] {
  const snapshot = [...messages];
  while (snapshot[snapshot.length - 1]?.role === "user") snapshot.pop();
  return snapshot.slice(-MAX_HISTORY_MESSAGES);
}

/**
 * Serialize the same bounded conversation window used by the native model.
 * Callers may pass the result downstream, but destructive routing must continue
 * to use the raw current-turn query separately.
 */
export function serializeChatHistoryContext(chatHistory?: ChatMessage[]): string {
  if (!chatHistory || chatHistory.length === 0) return "";

  const recentHistory = chatHistory.slice(-MAX_HISTORY_MESSAGES);
  const serializedTurns = recentHistory
    .map((msg) => {
      // Chat-mode and user turns carry prose in `content`. Successful edit-mode
      // assistant turns use the model-authored summary instead.
      const text =
        msg.content?.trim() ||
        (msg.role === "assistant" ? msg.modifications?.summary?.trim() : "") ||
        "";
      if (!text) return null;
      return `${msg.role === "user" ? "User" : "Assistant"}: ${text}`;
    })
    .filter((line): line is string => line !== null);

  return serializedTurns.length > 0
    ? "\n\n## Previous conversation:\n" + serializedTurns.join("\n") + "\n\n"
    : "";
}

/**
 * Build the rich prompt used by the PresLink backend only after raw-turn
 * routing succeeds. Keep the latest request last so it remains authoritative
 * for entity/date/metric interpretation.
 */
export function buildPreslinkProcessingPrompt(
  contextualCurrentTurnPrompt: string,
  chatHistory?: ChatMessage[]
): string {
  const historyContext = serializeChatHistoryContext(chatHistory).trim();
  const currentTurnPrompt = contextualCurrentTurnPrompt.trim();
  return historyContext ? `${historyContext}\n\n${currentTurnPrompt}` : currentTurnPrompt;
}

interface RequestContextStats {
  totalSlides: number;
  includedSlides: number;
  omittedSlides: number;
  truncated: boolean;
  contextChars: number;
}

export interface PreparedRequestData {
  contentToSend: PresentationContent;
  historyContext: string;
  textMessage: string;
  contextEnvelope: RequestContextEnvelope;
  contextStats: RequestContextStats;
}

interface SlideNumberMaps {
  slideNumberToIndex: Map<number, number>;
  indexToSlideNumber: Map<number, number>;
}

function buildSlideNumberMapsFromSortedSlides(sortedSlides: SlideContent[]): SlideNumberMaps {
  // `slideNumber` is the displayed PowerPoint slide number used for human
  // references. It can differ from `slide.index + 1` when the deck's
  // "Number slides from" setting starts at 0 or another custom offset.
  const slideNumberToIndex = new Map<number, number>();
  const indexToSlideNumber = new Map<number, number>();
  sortedSlides.forEach((slide) => {
    const slideNumber = slide.slideNumber ?? Math.max(1, slide.index + 1);
    slideNumberToIndex.set(slideNumber, slide.index);
    indexToSlideNumber.set(slide.index, slideNumber);
  });
  return { slideNumberToIndex, indexToSlideNumber };
}

/**
 * Host shape tags carry `DMSOFTTAG`, whose FileName/Path fields hold the user's
 * full local workbook path. The model only ever needs `preslinkLinked`, so tags
 * are dropped here — the single point where slide shapes enter model context.
 */
function withoutHostShapeTags(shapes: ShapeContent[]): ShapeContent[] {
  return shapes.map((shape) => {
    if (!shape.tags && !shape.shapes) return shape;
    const { tags: _tags, ...rest } = shape;
    return rest.shapes ? { ...rest, shapes: withoutHostShapeTags(rest.shapes) } : rest;
  });
}

function toSlideContextEntry(slide: SlideContent, slideNumber: number): SlideContextEntry {
  return {
    slideNumber,
    index: slide.index,
    id: slide.id,
    ...(slide.layout ? { layout: slide.layout } : {}),
    ...(slide.customLayoutName ? { customLayoutName: slide.customLayoutName } : {}),
    ...(slide.slideMasterName ? { slideMasterName: slide.slideMasterName } : {}),
    ...(typeof slide.displayMasterShapes === "boolean"
      ? { displayMasterShapes: slide.displayMasterShapes }
      : {}),
    ...(typeof slide.followMasterBackground === "boolean"
      ? { followMasterBackground: slide.followMasterBackground }
      : {}),
    shapes: withoutHostShapeTags(slide.shapes),
  };
}

export function buildRequestContextEnvelope(
  presentationContent: PresentationContent,
  maxDeckContextChars: number = MAX_DECK_CONTEXT_CHARS
): {
  contentToSend: PresentationContent;
  contextEnvelope: RequestContextEnvelope;
  contextStats: RequestContextStats;
} {
  const sortedSlides = [...presentationContent.slides].sort((a, b) => a.index - b.index);
  const fallbackSlideIndex =
    typeof presentationContent.selectedSlideIndex === "number"
      ? presentationContent.selectedSlideIndex
      : 0;
  const fallbackSlide: SlideContent = { id: "", index: fallbackSlideIndex, shapes: [] };
  const selectedSlide =
    sortedSlides.find((slide) => slide.index === presentationContent.selectedSlideIndex) ||
    sortedSlides[0] ||
    fallbackSlide;
  const { indexToSlideNumber } = buildSlideNumberMapsFromSortedSlides(sortedSlides);
  const getSlideNumber = (slide: SlideContent): number =>
    indexToSlideNumber.get(slide.index) ?? slide.slideNumber ?? Math.max(1, slide.index + 1);

  const selectedSlideEntry = toSlideContextEntry(selectedSlide, getSlideNumber(selectedSlide));
  let includedSlides: SlideContextEntry[] = [selectedSlideEntry];
  const includedSlideNumbers = new Set<number>([selectedSlideEntry.slideNumber]);
  // Prefer the true deck slide count when the producer set it (e.g. the
  // windowed chat extract sets `totalDeckSlides` from `GetSlideCount` so
  // the LLM sees `totalSlides=200` instead of `5` for a windowed deck).
  // Fall back to `sortedSlides.length` for unscoped extracts where every
  // slide is already present.
  const totalSlides = Math.max(
    presentationContent.totalDeckSlides ?? sortedSlides.length,
    sortedSlides.length,
    1
  );

  const estimateEnvelopeChars = (candidateSlides: SlideContextEntry[]): number => {
    const candidateDeckContext: DeckContext = {
      totalSlides,
      includedSlideCount: candidateSlides.length,
      truncated: false,
      omittedSlideNumbers: [],
      includedSlides: candidateSlides,
    };
    const candidateEnvelope: RequestContextEnvelope = {
      selectedSlideIndex: selectedSlide.index,
      selectedSlideNumber: selectedSlideEntry.slideNumber,
      slideWidth: presentationContent.slideWidth,
      slideHeight: presentationContent.slideHeight,
      currentSlideContext: selectedSlideEntry,
      deckContext: candidateDeckContext,
      ...(presentationContent.deckLayoutCatalog
        ? { deckLayoutCatalog: presentationContent.deckLayoutCatalog }
        : {}),
      ...(presentationContent.themeColors
        ? { themeColors: presentationContent.themeColors }
        : {}),
    };
    return JSON.stringify(candidateEnvelope).length;
  };

  for (const slide of sortedSlides) {
    const candidateEntry = toSlideContextEntry(slide, getSlideNumber(slide));
    if (includedSlideNumbers.has(candidateEntry.slideNumber)) {
      continue;
    }
    const candidateSlides = [...includedSlides, candidateEntry].sort((a, b) => a.index - b.index);
    const candidateChars = estimateEnvelopeChars(candidateSlides);
    if (candidateChars > maxDeckContextChars) {
      break;
    }
    includedSlides = candidateSlides;
    includedSlideNumbers.add(candidateEntry.slideNumber);
  }

  const allSlideNumbers = sortedSlides.map((slide) => getSlideNumber(slide));
  const omittedSlideNumbers = allSlideNumbers.filter(
    (slideNumber) => !includedSlideNumbers.has(slideNumber)
  );
  // `truncated` is `true` whenever the model is NOT seeing every deck
  // slide — either because the envelope budget dropped some (legacy
  // unscoped path) OR because the upstream producer already scoped the
  // extract to a window (windowed chat path: `totalSlides=145`,
  // `includedSlideCount=20`). The system prompt keys "do not assume you
  // have seen every slide" guidance off this flag, so it must fire for
  // both cases.
  const truncated = includedSlides.length < totalSlides;
  const deckContext: DeckContext = {
    totalSlides,
    includedSlideCount: includedSlides.length,
    truncated,
    omittedSlideNumbers,
    includedSlides,
  };

  const contextEnvelope: RequestContextEnvelope = {
    selectedSlideIndex: selectedSlide.index,
    selectedSlideNumber: selectedSlideEntry.slideNumber,
    slideWidth: presentationContent.slideWidth,
    slideHeight: presentationContent.slideHeight,
    currentSlideContext: selectedSlideEntry,
    deckContext,
    ...(presentationContent.deckLayoutCatalog
      ? { deckLayoutCatalog: presentationContent.deckLayoutCatalog }
      : {}),
    ...(presentationContent.themeColors
      ? { themeColors: presentationContent.themeColors }
      : {}),
  };

  const contentToSend: PresentationContent = {
    slides: includedSlides.map((slide) => ({
      id: slide.id,
      index: slide.index,
      slideNumber: slide.slideNumber,
      ...(slide.layout ? { layout: slide.layout } : {}),
      ...(slide.customLayoutName ? { customLayoutName: slide.customLayoutName } : {}),
      ...(slide.slideMasterName ? { slideMasterName: slide.slideMasterName } : {}),
      ...(typeof slide.displayMasterShapes === "boolean"
        ? { displayMasterShapes: slide.displayMasterShapes }
        : {}),
      ...(typeof slide.followMasterBackground === "boolean"
        ? { followMasterBackground: slide.followMasterBackground }
        : {}),
      shapes: slide.shapes,
    })),
    selectedSlideIndex: selectedSlide.index,
    slideWidth: presentationContent.slideWidth,
    slideHeight: presentationContent.slideHeight,
    deckLayoutCatalog: presentationContent.deckLayoutCatalog,
    themeColors: presentationContent.themeColors,
  };

  return {
    contentToSend,
    contextEnvelope,
    contextStats: {
      totalSlides,
      includedSlides: includedSlides.length,
      omittedSlides: omittedSlideNumbers.length,
      truncated: deckContext.truncated,
      contextChars: JSON.stringify(contextEnvelope).length,
    },
  };
}

export function prepareRequestData(
  presentationContent: PresentationContent,
  userQuery: string,
  chatHistory?: ChatMessage[]
): PreparedRequestData {
  const { contentToSend, contextEnvelope, contextStats } =
    buildRequestContextEnvelope(presentationContent);
  console.log(
    `[AI] Request context: totalSlides=${contextStats.totalSlides}, includedSlides=${contextStats.includedSlides}, omittedSlides=${contextStats.omittedSlides}, truncated=${contextStats.truncated}, contextSize=${(contextStats.contextChars / 1024).toFixed(1)}KB`
  );
  if (contextStats.truncated) {
    console.log(
      `[AI] Deck context truncated using selected+firstN policy (omitted slide numbers: ${contextEnvelope.deckContext.omittedSlideNumbers.join(", ") || "none"})`
    );
  }

  const historyContext = serializeChatHistoryContext(chatHistory);

  const textMessage = buildPrimaryRequestEnvelopePrompt({
    contextEnvelopeJson: JSON.stringify(contextEnvelope, null, 2),
    userQuery,
    historyContext,
  });

  return { contentToSend, historyContext, textMessage, contextEnvelope, contextStats };
}

export function buildToolFollowUpTextInput(
  presentationContent: PresentationContent,
  userQuery: string,
  followUpText: string,
  label: string
): string {
  const { contextEnvelope, contextStats } = buildRequestContextEnvelope(presentationContent);
  console.log(
    `[${label}] Request context: totalSlides=${contextStats.totalSlides}, includedSlides=${contextStats.includedSlides}, omittedSlides=${contextStats.omittedSlides}, truncated=${contextStats.truncated}, contextSize=${(contextStats.contextChars / 1024).toFixed(1)}KB`
  );
  if (contextStats.truncated) {
    console.log(
      `[${label}] Deck context truncated using selected+firstN policy (omitted slide numbers: ${contextEnvelope.deckContext.omittedSlideNumbers.join(", ") || "none"})`
    );
  }

  return buildToolFollowUpEnvelopePrompt({
    contextEnvelopeJson: JSON.stringify(contextEnvelope, null, 2),
    userQuery,
    followUpText,
  });
}
